/**
 * ResultsDisplay component - shows calculation results with charts and insights
 * Constitutional requirement: React with regular JavaScript, console logging, Chart.js
 */

import React, { useRef, useEffect } from 'react'
import { 
  AlertTriangle, 
  CreditCard, 
  Clock, 
  DollarSign, 
  BarChart3, 
  Target, 
  TrendingUp,
  NotebookText 
} from 'lucide-react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
)

const ResultsDisplay = ({ calculationResult }) => {
  console.log('📊 Calculation result:', calculationResult)

  const chartRefs = {
    balance: useRef(null),
    payments: useRef(null)
  }

  // Destroy charts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      Object.values(chartRefs).forEach(ref => {
        if (ref.current && ref.current.chart) {
          ref.current.chart.destroy()
        }
      })
    }
  }, [])

  // Always call the chart creation effect, but conditionally execute
  useEffect(() => {
    // Only create charts if we have a valid result that's not payment too small
    if (calculationResult && !calculationResult.isPaymentTooSmall && calculationResult.paymentSchedule) {
      console.log('📊 Creating charts for ResultsDisplay')
      
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        createBalanceChart()
        createPaymentsChart()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [calculationResult])

  // Early return check (after all hooks)
  if (!calculationResult) {
    console.log('⚠️ No calculation result provided to ResultsDisplay')
    return null
  }

  // Utility functions (moved up to be available for payment too small message)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  // Check if payment is too small to cover interest
  if (calculationResult.isPaymentTooSmall) {
    console.log('⚠️ Displaying payment too small message')
    return (
      <div className="results-display">
        <div className="payment-too-small-message">
          <div className="too-small-icon">
            <AlertTriangle size={32} color="#ff6b35" />
          </div>
          <h3>Payment Too Small</h3>
          <p className="too-small-description">
            Your monthly payment is insufficient to cover the interest charges on this debt.
          </p>
          
          <div className="payment-analysis">
            <div className="analysis-item">
              <span className="analysis-label">Your Monthly Payment:</span>
              <span className="analysis-value">{formatCurrency(calculationResult.input.monthlyPayment)}</span>
            </div>
            <div className="analysis-item">
              <span className="analysis-label">Monthly Interest Charge:</span>
              <span className="analysis-value warning">{formatCurrency(calculationResult.monthlyInterestCharge)}</span>
            </div>
            <div className="analysis-item highlight">
              <span className="analysis-label">Minimum Payment Needed:</span>
              <span className="analysis-value recommended">{formatCurrency(calculationResult.minimumPaymentNeeded)}</span>
            </div>
          </div>
          
          <div className="recommendation">
            <h4>💡 Recommendation</h4>
            <p>
              To make progress on your debt, you need to pay at least <strong>{formatCurrency(calculationResult.minimumPaymentNeeded)}</strong> per month. 
              This amount covers the interest charges plus a small amount toward the principal balance.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const { input, paymentSchedule, summary } = calculationResult

  console.log('Payment Schedule:', paymentSchedule)

  // Determine calculation mode based on input or calculation characteristics
  // const isMonthlyPaymentCalculation = input.calculationMode === 'monthlyPayment' || 
  //   (paymentSchedule.length > 0 && input.monthlyPayment === paymentSchedule[0].payment)
  const isMonthlyPaymentCalculation = input.calculationMode === 'monthlyPayment' 

  console.log('🔍 Calculation mode detection:', {
    inputMode: input.calculationMode,
    isMonthlyPaymentCalc: isMonthlyPaymentCalculation
  })

  const formatTimespan = (months) => {
    const years = Math.floor(months / 12)
    const remainingMonths = months % 12
    
    if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`
    } else {
      return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`
    }
  }

  // Chart creation functions
  const createBalanceChart = () => {
    const canvas = chartRefs.balance.current
    if (!canvas) {
      console.log('⚠️ Balance chart canvas not found')
      return
    }

    const ctx = canvas.getContext('2d')
    
    // Destroy existing chart if it exists
    if (canvas.chart) {
      canvas.chart.destroy()
    }

    console.log('📊 Payment Schedule Data:', paymentSchedule)
    console.log('📊 First few entries:', paymentSchedule.slice(0, 3))

    const months = paymentSchedule.map(entry => `Month ${entry.month}`)
    const balances = paymentSchedule.map(entry => entry.endingBalance) // Changed from remainingBalance

    console.log('🎨 Creating balance chart with', months.length, 'data points')
    console.log('📈 Balance data:', balances.slice(0, 5))

    canvas.chart = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels: months,
        datasets: [{
          label: 'Remaining Balance',
          data: balances,
          borderColor: '#ff6b35',  // Orange color
          backgroundColor: 'rgba(255, 107, 53, 0.1)',  // Light orange
          fill: true,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Debt Balance Over Time'
          },
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return formatCurrency(value)
              }
            }
          },
          x: {
            display: months.length <= 60 // Show x-axis labels only for short schedules
          }
        }
      }
    })
  }

  const createPaymentsChart = () => {
    const canvas = chartRefs.payments.current
    if (!canvas) {
      console.log('⚠️ Payments chart canvas not found')
      return
    }

    const ctx = canvas.getContext('2d')
    
    // Destroy existing chart if it exists
    if (canvas.chart) {
      canvas.chart.destroy()
    }

    const months = paymentSchedule.map(entry => `Month ${entry.month}`)
    const principalPayments = paymentSchedule.map(entry => entry.principalPayment)
    const interestPayments = paymentSchedule.map(entry => entry.interestCharge) // Changed from interestPayment

    console.log('🎨 Creating payments chart with', months.length, 'data points')
    console.log('💰 Principal payments:', principalPayments.slice(0, 5))
    console.log('💸 Interest payments:', interestPayments.slice(0, 5))

    canvas.chart = new ChartJS(ctx, {
      type: 'bar',
      data: {
        labels: months,
        datasets: [
          {
            label: 'Principal',
            data: principalPayments,
            backgroundColor: '#ff6b35',  // Orange color
            stack: 'payments'
          },
          {
            label: 'Interest',
            data: interestPayments,
            backgroundColor: '#666666',  // Grey color
            stack: 'payments'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Monthly Payment Breakdown'
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            stacked: true,
            ticks: {
              callback: function(value) {
                return formatCurrency(value)
              }
            }
          },
          x: {
            stacked: true,
            display: months.length <= 60 // Show x-axis labels only for short schedules
          }
        }
      }
    })
  }

  return (
    <div className="results-display">
      
      {/* Results Summary */}
      <div className="results-summary">
        <h3>
          <BarChart3 size={20} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
          Calculation Results
        </h3>
        
        <div className="summary-grid">
          <div className="summary-card primary">
            <div className="card-icon">
              {isMonthlyPaymentCalculation ? 
                <CreditCard size={24} color="#fff" /> : 
                <Clock size={24} color="#fff" />
              }
            </div>
            <div className="card-content">
              <span className="card-label">
                {isMonthlyPaymentCalculation ? 'Required Payment - ' : 'Payoff Time '}
              </span>
              <span className="card-value">
                {isMonthlyPaymentCalculation 
                  ? formatCurrency(paymentSchedule[0]?.payment || 0)
                  : formatTimespan(summary.totalMonths)
                }
              </span>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">
              <DollarSign size={24} color="#fff" />
            </div>
            <div className="card-content">
              <span className="card-label">Total Interest - </span>
              <span className="card-value">{formatCurrency(summary.totalInterest)}</span>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">
              <CreditCard size={24} color="#fff" />
            </div>
            <div className="card-content">
              <span className="card-label">Total Payments - </span>
              <span className="card-value">{formatCurrency(summary.totalPayments)}</span>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">
              <TrendingUp size={24} color="#fff" />
            </div>
            <div className="card-content">
              <span className="card-label">Interest Percentage - </span>
              <span className="card-value">{((summary.totalInterest / summary.totalPayments) * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Calculation Details */}
        <div className="calculation-details">
          <h4>
            <NotebookText size={20} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
             Calculation Details
          </h4>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Starting Balance:</span>
              <span className="detail-value">{formatCurrency(input.balance)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Interest Rate (APR):</span>
              <span className="detail-value">{input.interestRate}%</span>
            </div>
            {/* {isMonthlyPaymentCalculation ? ( */}
              <div className="detail-item">
                <span className="detail-label">Target Payoff Time:</span>
                <span className="detail-value">{formatTimespan(summary.totalMonths)}</span>
              </div>
            {/* ) : ( */}
              <div className="detail-item">
                <span className="detail-label">Monthly Payment:</span>
                <span className="detail-value">{formatCurrency(input.monthlyPayment)}</span>
              </div>
            {/* )} */}
            <div className="detail-item">
              <span className="detail-label">Calculation Method:</span>
              <span className="detail-value">
                Compound Interest (Daily)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        
        {/* Balance Over Time Chart */}
        <div className="chart-container">
          <div className="chart-wrapper">
            <canvas ref={chartRefs.balance}></canvas>
          </div>
        </div>

        {/* Payment Breakdown Chart */}
        <div className="chart-container">
          <div className="chart-wrapper">
            <canvas ref={chartRefs.payments}></canvas>
          </div>
        </div>

      </div>

      {/* Key Insights */}
      <div className="insights-section">
        <h4>
          <Target size={16} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
          Key Insights
        </h4>
        <div className="insights-list">
          {isMonthlyPaymentCalculation ? (
            <>
              <div className="insight">
                <span className="insight-icon">
                  <Target size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  You need to pay <strong>{formatCurrency(paymentSchedule[0]?.payment || 0)}</strong> monthly to pay off your debt in exactly <strong>{formatTimespan(summary.totalMonths)}</strong>.
                </span>
              </div>
              <div className="insight">
                <span className="insight-icon">
                  <DollarSign size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  You'll pay <strong>{formatCurrency(summary.totalInterest)}</strong> in interest charges with this targeted payoff timeline.
                </span>
              </div>
              <div className="insight">
                <span className="insight-icon">
                  <BarChart3 size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  Interest represents <strong>{((summary.totalInterest / summary.totalPayments) * 100).toFixed(1)}%</strong> of your total payments.
                </span>
              </div>
              {summary.monthlyPayment > input.balance * 0.1 && (
                <div className="insight warning">
                  <span className="insight-icon">
                    <AlertTriangle size={16} color="#ff6b35" />
                  </span>
                  <span className="insight-text">
                    This payment amount is quite high relative to your balance. Consider a longer payoff period if needed.
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="insight">
                <span className="insight-icon">
                  <Clock size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  You'll be debt-free in <strong>{formatTimespan(summary.totalMonths)}</strong> with your current payment plan.
                </span>
              </div>
              <div className="insight">
                <span className="insight-icon">
                  <DollarSign size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  You'll pay <strong>{formatCurrency(summary.totalInterest)}</strong> in interest charges over the life of the debt.
                </span>
              </div>
              <div className="insight">
                <span className="insight-icon">
                  <BarChart3 size={16} color="#ff6b35" />
                </span>
                <span className="insight-text">
                  Interest represents <strong>{((summary.totalInterest / summary.totalPayments) * 100).toFixed(1)}%</strong> of your total payments.
                </span>
              </div>
              {summary.totalInterest > input.balance * 0.5 && (
                <div className="insight warning">
                  <span className="insight-icon">
                    <AlertTriangle size={16} color="#ff6b35" />
                  </span>
                  <span className="insight-text">
                    Consider increasing your monthly payment to reduce the total interest paid.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  )
}

export default ResultsDisplay
